import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Loader2, Save, CheckCircle, XCircle, Upload, FileText, Trash2, UserCheck, UserX, ExternalLink, ClipboardCopy, Mail, ArrowRight, ArrowLeft } from 'lucide-react';
import { formatCPF, formatCNPJ, formatPhone, formatCEP, formatMoney, onlyNumbers, validateCPF, validatePhone, validateCEP, validateDateBR } from '../../lib/helpers';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const FIELD_TYPES = {
  text: { component: 'input', props: { type: 'text' } },
  textarea: { component: 'textarea', props: {} },
  date: { component: 'input', props: { type: 'date' } },
  select: { component: 'select', props: {} },
  money: { component: 'input', props: { type: 'text' } },
};

const RHProcessFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [step, setStep] = useState(isEditing ? 'form' : 'tipo');
  const [tipoSelecionado, setTipoSelecionado] = useState('');
  const [regimeSelecionado, setRegimeSelecionado] = useState('');
  const [processoId, setProcessoId] = useState(id);
  const [processo, setProcesso] = useState(null);
  const [fields, setFields] = useState([]);
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [attachedDocs, setAttachedDocs] = useState([]);
  const [fieldsData, setFieldsData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [concluding, setConcluding] = useState(false);
  const [solicitando, setSolicitando] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [funcionarios, setFuncionarios] = useState([]);
  const [funcionariosFiltrados, setFuncionariosFiltrados] = useState([]);
  const [funcionarioSearch, setFuncionarioSearch] = useState('');
  const [loadingFuncionarios, setLoadingFuncionarios] = useState(false);
  const [solicitacao, setSolicitacao] = useState(null);
  const [showSolicitacaoModal, setShowSolicitacaoModal] = useState(false);
  const [solicitacaoLink, setSolicitacaoLink] = useState('');
  const [cargos, setCargos] = useState([]);
  const [documentosSolicitar, setDocumentosSolicitar] = useState([]);
  const [emailDestinatario, setEmailDestinatario] = useState('');
  const [nomeDestinatario, setNomeDestinatario] = useState('');
  const [cpfDestinatario, setCpfDestinatario] = useState('');

  useEffect(() => {
    if (isEditing) loadProcesso();
  }, [id]);

  useEffect(() => {
    if (step === 'form' && tipoSelecionado && regimeSelecionado) {
      loadSchema();
      loadCargos();
    }
  }, [step, tipoSelecionado, regimeSelecionado]);

  const hasPendingSignature = attachedDocs.some(d => d.signature_status === 'sent');

  useEffect(() => {
    if (!hasPendingSignature || tipoSelecionado !== 'desligamento') return;
    const interval = setInterval(() => {
      loadProcesso();
    }, 5000);
    return () => clearInterval(interval);
  }, [hasPendingSignature, tipoSelecionado]);

  const loadCargos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/position-salaries/cargos`, { headers: { Authorization: `Bearer ${token}` } });
      setCargos(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar cargos');
    }
  };

  const loadSchema = async () => {
    try {
      const token = localStorage.getItem('token');
      const [fieldsRes, docsRes] = await Promise.all([
        axios.get(`${API}/api/rh/process-fields`, { params: { process_type_slug: tipoSelecionado, contract_type: regimeSelecionado }, headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/rh/required-documents`, { params: { process_type_slug: tipoSelecionado, contract_type: regimeSelecionado }, headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setFields(fieldsRes.data);
      setRequiredDocs(docsRes.data);
      if (!isEditing) {
        setDocumentosSolicitar(docsRes.data.map(d => d.nome));
      }
    } catch (error) {
      toast.error('Erro ao carregar schema do formulário');
    }
  };

  const loadProcesso = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/rh/processos/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      const { processo, fields: f, required_documents: rd, attached_documents: ad } = response.data;
      setProcesso(processo);
      setTipoSelecionado(processo.process_type_slug);
      setRegimeSelecionado(processo.contract_type);
      setFields(f);
      setRequiredDocs(rd);
      setAttachedDocs(ad);
      setFieldsData(processo.fields_data || {});
      setDocumentosSolicitar(processo.documentos_solicitar || []);
      const fd = processo.fields_data || {};
      setEmailDestinatario(processo.employee_email || fd.email || '');
      setNomeDestinatario(processo.employee_nome || fd.nome_completo || '');
      setCpfDestinatario(formatCPF(processo.employee_cpf || fd.cpf || fd.cpf_cnpj || ''));
    } catch (error) {
      toast.error('Erro ao carregar processo');
      navigate('/rh/admissoes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProcesso = async () => {
    if (!tipoSelecionado || !regimeSelecionado) {
      toast.error('Selecione o tipo e o regime');
      return;
    }
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const data = { process_type_slug: tipoSelecionado, contract_type: regimeSelecionado };
      if (tipoSelecionado === 'desligamento') {
        await loadFuncionarios();
        setStep('select_funcionario');
        setLoading(false);
        return;
      }
      const response = await axios.post(`${API}/api/rh/processos`, data, { headers: { Authorization: `Bearer ${token}` } });
      setProcessoId(response.data.id);
      setStep('form');
      toast.success('Processo criado!');
    } catch (error) {
      console.error('Erro ao criar processo:', error);
      toast.error(error.response?.data?.detail || error.message || 'Erro ao criar processo');
    } finally {
      setLoading(false);
    }
  };

  const loadFuncionarios = async (searchTerm = '') => {
    try {
      setLoadingFuncionarios(true);
      const token = localStorage.getItem('token');
      const params = { active_only: true };
      if (regimeSelecionado) params.regime = regimeSelecionado;
      if (searchTerm.trim()) params.search = searchTerm.trim();
      const response = await axios.get(`${API}/api/employees`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setFuncionarios(response.data);
      setFuncionariosFiltrados(response.data);
    } catch (error) {
      console.error('Erro ao carregar funcionários:', error);
      toast.error('Erro ao carregar funcionários');
    } finally {
      setLoadingFuncionarios(false);
    }
  };

  const handleFuncionarioSearch = (value) => {
    setFuncionarioSearch(value);
    loadFuncionarios(value);
  };

  const handleCreateWithFuncionario = async (funcionarioId, funcionarioNome, funcionarioEmail, funcionarioCpf) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const payload = {
        process_type_slug: tipoSelecionado,
        contract_type: regimeSelecionado,
        employee_id: funcionarioId,
        employee_nome: funcionarioNome,
        employee_email: funcionarioEmail || '',
        employee_cpf: funcionarioCpf || '',
      };
      console.log('Criando processo com payload:', payload);
      const response = await axios.post(`${API}/api/rh/processos`, payload, { headers: { Authorization: `Bearer ${token}` } });
      console.log('Processo criado:', response.data);
      setProcessoId(response.data.id);
      setEmailDestinatario(response.data.employee_email || funcionarioEmail || '');
      setStep('form');
      toast.success('Processo de desligamento criado!');
    } catch (error) {
      console.error('Erro ao criar processo desligamento:', error);
      console.error('Resposta completa:', error.response);
      console.error('Resposta data:', error.response?.data);
      toast.error(error.response?.data?.detail || error.message || 'Erro ao criar processo');
    } finally {
      setLoading(false);
    }
  };

  const loadSolicitacao = useCallback(async () => {
    if (!processoId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/rh/processos/${processoId}/solicitacao`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.solicitacao) {
        setSolicitacao(response.data.solicitacao);
      }
    } catch (error) {
      console.error('Erro ao carregar solicitação');
    }
  }, [processoId]);

  useEffect(() => {
    if (processoId) loadSolicitacao();
  }, [processoId, loadSolicitacao]);

  const handleSolicitarDocumentos = async () => {
    if (!documentosSolicitar.length) {
      toast.error('Selecione ao menos um documento para solicitar');
      return;
    }
    const nomeFinal = nomeDestinatario || fieldsData.nome_completo || '';
    const cpfFinal = onlyNumbers(cpfDestinatario || fieldsData.cpf || fieldsData.cpf_cnpj || '');
    if (!emailDestinatario || !nomeFinal || !cpfFinal) {
      toast.error('Preencha nome, CPF e email do destinatário');
      return;
    }
    try {
      setSolicitando(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/rh/processos/${processoId}/solicitar-documentos`, {
        documentos_solicitar: documentosSolicitar,
        email_destinatario: emailDestinatario,
        nome_destinatario: nomeFinal,
        cpf_destinatario: cpfFinal,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSolicitacaoLink(response.data.link);
      setShowSolicitacaoModal(true);
      await loadSolicitacao();
      toast.success('Solicitação de documentos enviada ao colaborador!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar solicitação');
    } finally {
      setSolicitando(false);
    }
  };

  const handleEnviarAssinatura = async () => {
    const docsParaAssinar = attachedDocs.filter(d => d.selectedForSignature);
    if (!docsParaAssinar.length) {
      toast.error('Selecione ao menos um documento para enviar para assinatura');
      return;
    }
    try {
      setSolicitando(true);
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API}/api/rh/processos/${processoId}/enviar-assinatura`, {
        document_ids: docsParaAssinar.map(d => d.id),
      }, { headers: { Authorization: `Bearer ${token}` } });
      const results = r.data?.results || [];
      setAttachedDocs(prev => prev.map(d => {
        const found = results.find(r => r.document_id === d.id);
        if (found) {
          return { ...d, signature_status: found.status, signing_urls: found.signing_urls };
        }
        return d;
      }));
      await loadSolicitacao();
      toast.success(`${results.filter(r => r.status === 'sent').length} documento(s) enviado(s) para assinatura!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar para assinatura');
    } finally {
      setSolicitando(false);
    }
  };

  const handleConclude = async () => {
    if (tipoSelecionado !== 'desligamento' && solicitacao && solicitacao.status !== 'finalizado') {
      toast.error('Aguardando conclusão da solicitação de documentos pelo colaborador');
      return;
    }
    if (!window.confirm('Concluir este processo? Esta ação não pode ser desfeita.')) return;
    try {
      setConcluding(true);
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/rh/processos/${processoId}`, {
        fields_data: fieldsData,
        documentos_solicitar: documentosSolicitar
      }, { headers: { Authorization: `Bearer ${token}` } });
      const response = await axios.post(`${API}/api/rh/processos/${processoId}/concluir`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Processo concluído com sucesso!');
      navigate(`/rh/${tipoSelecionado === 'admissao' ? 'admissoes' : 'desligamentos'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao concluir processo');
    } finally {
      setConcluding(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/rh/processos/${processoId}`, {
        fields_data: fieldsData,
        documentos_solicitar: documentosSolicitar
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Rascunho salvo!');
    } catch (error) {
      toast.error('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e, docNome, requiredDocId) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('nome', docNome);
      if (requiredDocId) formData.append('required_document_id', requiredDocId);
      const response = await axios.post(`${API}/api/rh/processos/${processoId}/documentos/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setAttachedDocs(prev => [...prev, response.data]);
      toast.success('Documento anexado!');
    } catch (error) {
      toast.error('Erro ao fazer upload');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveDoc = async (docId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/rh/processos/${processoId}/documentos/${docId}`, { headers: { Authorization: `Bearer ${token}` } });
      setAttachedDocs(prev => prev.filter(d => d.id !== docId));
      toast.success('Documento removido');
    } catch (error) {
      toast.error('Erro ao remover documento');
    }
  };

  const renderField = (field) => {
    const value = fieldsData[field.field_key] || '';
    const setValue = (val) => setFieldsData(prev => ({ ...prev, [field.field_key]: val }));

    if (field.field_key === 'cargo') {
      return (
        <Select value={value} onValueChange={setValue}>
          <SelectTrigger><SelectValue placeholder="Selecione o cargo" /></SelectTrigger>
          <SelectContent>
            {cargos.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }

    if (field.field_key === 'cpf' || field.field_key === 'cpf_cnpj') {
      const masked = field.field_key === 'cpf' ? formatCPF(value) : (value.length > 14 ? formatCNPJ(value) : formatCPF(value));
      return <Input type="text" value={masked} onChange={(e) => setValue(e.target.value)} placeholder={field.field_label} maxLength={field.field_key === 'cpf' ? 14 : 18} />;
    }

    if (field.field_key === 'telefone') {
      return <Input type="text" value={formatPhone(value)} onChange={(e) => setValue(e.target.value)} placeholder="(11) 99999-9999" maxLength={16} />;
    }

    if (field.field_key === 'cep') {
      return <Input type="text" value={formatCEP(value)} onChange={(e) => setValue(e.target.value)} placeholder="00000-000" maxLength={9} />;
    }

    if (field.field_type === 'textarea') {
      return <Textarea value={value} onChange={(e) => setValue(e.target.value)} placeholder={field.field_label} />;
    }
    if (field.field_type === 'select') {
      const options = field.options_json ? JSON.parse(field.options_json) : [];
      return (
        <Select value={value} onValueChange={setValue}>
          <SelectTrigger><SelectValue placeholder={`Selecione ${field.field_label}`} /></SelectTrigger>
          <SelectContent>
            {options.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    if (field.field_type === 'money') {
      return (
        <Input
          type="text"
          value={value}
          onChange={(e) => setValue(formatMoney(e.target.value))}
          placeholder="0,00"
        />
      );
    }
    return <Input type={field.field_type === 'date' ? 'date' : 'text'} value={value} onChange={(e) => setValue(e.target.value)} placeholder={field.field_label} />;
  };

  const isRequiredDocAttached = (nome) => attachedDocs.some(d => d.nome === nome);

  if (loading && isEditing) {
    return <div className="p-6 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  if (step === 'tipo') {
    return (
      <div className="p-6 max-w-xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Novo Processo de RH</h1>
          <p className="text-sm text-gray-500">Selecione o tipo e o regime de contratação</p>
        </div>
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="space-y-2">
              <Label>Tipo de Processo *</Label>
              <Select value={tipoSelecionado} onValueChange={setTipoSelecionado}>
                <SelectTrigger><SelectValue placeholder="Selecione o tipo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admissao">Admissão</SelectItem>
                  <SelectItem value="desligamento">Desligamento</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Regime de Contratação *</Label>
              <Select value={regimeSelecionado} onValueChange={setRegimeSelecionado}>
                <SelectTrigger><SelectValue placeholder="Selecione o regime" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="clt">CLT</SelectItem>
                  <SelectItem value="pj">PJ</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleCreateProcesso} className="w-full" disabled={!tipoSelecionado || !regimeSelecionado || loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
              {tipoSelecionado === 'desligamento' ? 'Selecionar Funcionário' : 'Criar Processo'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 'select_funcionario') {
    const displayList = funcionarioSearch.trim() ? funcionariosFiltrados : funcionarios;
    return (
      <div className="p-6 max-w-xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Selecionar Funcionário</h1>
          <p className="text-sm text-gray-500">
            Escolha o funcionário para desligamento ({regimeSelecionado?.toUpperCase()})
          </p>
        </div>
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="space-y-2">
              <Label>Buscar funcionário</Label>
              <Input
                placeholder="Digite o nome ou CPF do funcionário..."
                value={funcionarioSearch}
                onChange={(e) => handleFuncionarioSearch(e.target.value)}
              />
            </div>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {loadingFuncionarios ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : displayList.length > 0 ? (
                displayList.map(f => (
                  <Button key={f.id} variant="outline" className="w-full justify-start text-left" onClick={() => handleCreateWithFuncionario(f.id, f.nome, f.email, f.cpf)}>
                    <div>
                      <div>{f.nome}</div>
                      <div className="text-xs text-gray-500">{f.cargo || 'Sem cargo'} {f.email ? `• ${f.email}` : ''}</div>
                    </div>
                  </Button>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">
                  {funcionarioSearch.trim() ? 'Nenhum funcionário encontrado' : `Nenhum funcionário ${regimeSelecionado?.toUpperCase()} ativo encontrado`}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const sections = [...new Set(fields.map(f => f.section).filter(Boolean))];
  const fieldsBySection = section => fields.filter(f => (f.section || 'Geral') === section);
  const allSections = sections.length > 0 ? sections : ['Geral'];

  const obrigatoriosNomes = requiredDocs.filter(d => d.obrigatorio).map(d => d.nome);
  const docsConcluidos = solicitacao ? solicitacao.status === 'finalizado' : (obrigatoriosNomes.length > 0 && obrigatoriosNomes.every(isRequiredDocAttached));
  const exameConcluido = processo?.exame_admissional?.status === 'concluido';
  const admissaoEtapasPendentes = tipoSelecionado === 'admissao' && docsConcluidos && !exameConcluido;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(isEditing ? `/rh/processos/${processoId}` : (tipoSelecionado === 'desligamento' ? '/rh/desligamentos' : '/rh/admissoes'))}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Processo de {TIPO_CONFIG[tipoSelecionado]?.label || tipoSelecionado}
            </h1>
            <p className="text-sm text-gray-500">Regime: {regimeSelecionado?.toUpperCase()}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Salvar Rascunho
          </Button>
          {admissaoEtapasPendentes ? (
            <Button onClick={() => navigate(`/rh/processos/${processoId}/exame-admissional`)}>
              <ArrowRight className="h-4 w-4 mr-2" />
              Configurar Exame Admissional
            </Button>
          ) : (
            <Button onClick={handleConclude} disabled={concluding || (tipoSelecionado !== 'desligamento' && solicitacao && solicitacao.status !== 'finalizado')}>
              {concluding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              {tipoSelecionado !== 'desligamento' && solicitacao && solicitacao.status !== 'finalizado' ? 'Aguardando Documentos...'
                : attachedDocs.some(d => d.signature_status === 'sent') ? 'Aguardando Assinatura...'
                : 'Concluir'}
            </Button>
          )}
        </div>
      </div>

      {allSections.map(section => (
        <Card key={section}>
          <CardHeader><CardTitle>{section}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {fieldsBySection(section).map(field => (
              <div key={field.field_key} className="space-y-1">
                <Label>
                  {field.field_label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                  {field.sync_to_employee && <Badge variant="outline" className="ml-2 text-xs">Salvo no cadastro</Badge>}
                </Label>
                {renderField(field)}
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      <Card>
        <CardHeader>
          <CardTitle>Documentos Anexados</CardTitle>
          <CardDescription>{attachedDocs.length} de {requiredDocs.length} documentos anexados</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {requiredDocs.filter(doc => !isRequiredDocAttached(doc.nome)).length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">Todos os documentos foram anexados</p>
          ) : requiredDocs.filter(doc => !isRequiredDocAttached(doc.nome)).map(doc => (
            <div key={doc.id || doc.nome} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-gray-500" />
                <span className="text-sm">{doc.nome}</span>
                {doc.obrigatorio && <Badge variant="outline" className="text-xs">Obrigatório</Badge>}
              </div>
              <div className="relative">
                <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => handleFileUpload(e, doc.nome, doc.id)} accept=".pdf,.jpg,.jpeg,.png" />
                <Button variant="outline" size="sm" disabled={uploading}>
                  <Upload className="h-4 w-4 mr-1" /> Anexar
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            {tipoSelecionado === 'desligamento' ? 'Enviar Documentos para Assinatura do Colaborador' : 'Solicitar Documentos ao Colaborador'}
          </CardTitle>
          <CardDescription>
            {tipoSelecionado === 'desligamento'
              ? 'Selecione os documentos anexados que o colaborador deve assinar digitalmente.'
              : 'Selecione os documentos que o colaborador deve enviar. Uma solicitação será enviada por email com link para upload.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {tipoSelecionado === 'desligamento' ? (
            <>
              {attachedDocs.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  Nenhum documento anexado. Anexe os documentos primeiro.
                </p>
              ) : (
                <>
                  {attachedDocs.map(doc => {
                    const sigStatus = doc.signature_status;
                    const isSent = sigStatus === 'sent';
                    const isSigned = sigStatus === 'signed';
                    return (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2 flex-1">
                          <Checkbox
                            checked={doc.selectedForSignature || false}
                            disabled={isSent || isSigned}
                            onCheckedChange={(checked) => {
                              setAttachedDocs(prev => prev.map(d =>
                                d.id === doc.id ? { ...d, selectedForSignature: checked } : d
                              ));
                            }}
                          />
                          <FileText className="h-4 w-4 text-gray-500 shrink-0" />
                          <span className="text-sm">{doc.nome}</span>
                          {isSigned && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">Assinado</Badge>}
                          {isSent && !isSigned && <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">Enviado</Badge>}
                        </div>
                        {isSent && doc.signing_urls?.length > 0 && (
                          <Button variant="ghost" size="sm" onClick={() => {
                            navigator.clipboard.writeText(doc.signing_urls[0].signing_url);
                            toast.success('Link de assinatura copiado!');
                          }}>
                            <ClipboardCopy className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                  {attachedDocs.some(d => !d.signature_status) && (
                    <Button onClick={handleEnviarAssinatura} className="w-full" disabled={solicitando || !attachedDocs.some(d => d.selectedForSignature)}>
                      {solicitando ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
                      Enviar para Assinatura
                    </Button>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              {solicitacao ? (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 flex items-center gap-2">
                    <Mail className="h-4 w-4 shrink-0" />
                    Solicitação enviada para <strong>{solicitacao.email_destinatario}</strong>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Status</Label>
                    <div className="mt-1 space-y-1">
                      {solicitacao.documentos_esperados?.map(doc => {
                        const enviado = solicitacao.documentos_enviados?.find(e => e.tipo_documento_id === doc.tipo_documento_id);
                        const statusLabel = enviado ? (enviado.status === 'aprovado' ? 'Aprovado' : enviado.status === 'recusado' ? 'Recusado' : 'Enviado') : 'Pendente';
                        const statusColor = enviado ? (enviado.status === 'aprovado' ? 'text-green-600' : enviado.status === 'recusado' ? 'text-red-600' : 'text-blue-600') : 'text-gray-400';
                        return (
                          <div key={doc.tipo_documento_id} className="flex items-center justify-between text-sm">
                            <span>{doc.tipo_documento_nome}</span>
                            <span className={statusColor}>{statusLabel}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {solicitacao.status !== 'finalizado' && (
                    <Button variant="outline" size="sm" className="w-full" onClick={() => window.open('/documentos/rh/solicitacoes', '_blank')}>
                      <ExternalLink className="h-4 w-4 mr-2" /> Gerenciar Solicitação
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nome do destinatário *</Label>
                      <Input
                        value={nomeDestinatario || fieldsData.nome_completo || ''}
                        onChange={(e) => setNomeDestinatario(e.target.value)}
                        placeholder="Nome completo do colaborador"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>CPF do destinatário *</Label>
                      <Input
                        value={formatCPF(cpfDestinatario || fieldsData.cpf || fieldsData.cpf_cnpj || '')}
                        onChange={(e) => setCpfDestinatario(e.target.value)}
                        placeholder="000.000.000-00"
                        maxLength={14}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Email do destinatário *</Label>
                    <Input
                      type="email"
                      value={emailDestinatario}
                      onChange={(e) => setEmailDestinatario(e.target.value)}
                      placeholder="email@exemplo.com"
                    />
                  </div>
                  <div className="space-y-2">
                    {requiredDocs.map(doc => {
                      const isChecked = documentosSolicitar.some(d => d === doc.nome || d?.nome === doc.nome);
                      return (
                        <label key={doc.id || doc.nome} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
                          <Checkbox
                            checked={isChecked}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setDocumentosSolicitar(prev => [...prev, doc.nome]);
                              } else {
                                setDocumentosSolicitar(prev => prev.filter(d => d !== doc.nome && d?.nome !== doc.nome));
                              }
                            }}
                          />
                          <FileText className="h-4 w-4 text-gray-500 shrink-0" />
                          <span className="text-sm">{doc.nome}</span>
                          {doc.obrigatorio && <Badge variant="outline" className="text-xs">Obrigatório</Badge>}
                        </label>
                      );
                    })}
                  </div>
                  <Button onClick={handleSolicitarDocumentos} className="w-full" disabled={solicitando || !documentosSolicitar.length}>
                    {solicitando ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
                    Enviar Solicitação
                  </Button>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {tipoSelecionado !== 'desligamento' && (
        <Dialog open={showSolicitacaoModal} onOpenChange={setShowSolicitacaoModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Solicitação de Documentos Enviada</DialogTitle>
              <DialogDescription>
                Uma solicitação de documentos foi enviada para o colaborador. Ele receberá um email com o link para upload.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3">
                <Mail className="h-5 w-5 text-blue-600 shrink-0" />
                <div className="text-sm text-blue-800">
                  Email enviado com link e senha de acesso
                </div>
              </div>
              {solicitacaoLink && (
                <div className="space-y-2">
                  <Label className="text-xs text-gray-500">Link de Acesso</Label>
                  <div className="flex items-center gap-2">
                    <Input value={solicitacaoLink} readOnly className="text-xs" />
                    <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(solicitacaoLink); toast.success('Link copiado!'); }}>
                      <ClipboardCopy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => window.open('/documentos/rh/solicitacoes', '_blank')}>
                  <ExternalLink className="h-4 w-4 mr-2" /> Ver Solicitações
                </Button>
                <Button onClick={() => { setShowSolicitacaoModal(false); }}>
                  Ok
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const TIPO_CONFIG = {
  admissao: { label: 'Admissão', icon: UserCheck },
  desligamento: { label: 'Desligamento', icon: UserX },
};

export default RHProcessFormPage;
